import { computed } from "vue";
import { resolveLocalizedTitle } from "./useCardData";
import { rankingPositions } from "./useChartRules";

/**
 * Matrix and stacked chart data composable — builds stacked bar data,
 * choice codes/labels, and matrix table structures.
 */
export function useMatrixChartData(ctx, { populationsBarData }) {
  const {
    chartsData,
    displayLocale,
    cardType,
    questionMeta,
    isMatrix,
    activeItems,
    codeLabels,
    includeSkips,
    choiceColors,
    matrixPopulations,
    effectiveQuestionId,
    getAdjustedBase,
    getSkippedCount,
    getAnswerCount,
    getAnswerPercentage,
    getAnswerDisplayOrder,
    getSignificance,
  } = ctx;

  // --- Ranking: one stacked bar per answer, one segment per position ---

  const isRanking = computed(() => cardType.value === "q_ranking");
  const rankingList = computed(() => (isRanking.value ? rankingPositions(questionMeta.value) : []));
  const rankingCodes = computed(() => rankingList.value.map((p) => p.code));
  const rankingLabels = computed(() => Object.fromEntries(rankingList.value.map((p) => [p.code, p.label])));

  // Answers outer, populations inner, like the matrix.
  const rankingStackedData = computed(() => {
    if (!isRanking.value) return [];
    const allRaw = chartsData.value[effectiveQuestionId.value];
    if (!allRaw) return [];
    const pops = matrixPopulations.value;
    const multiPop = pops.length > 1;
    const sources = pops
      .map((pop) => ({ pop, source: pop.key ? allRaw[pop.key] : allRaw }))
      .filter(({ source }) => source?.answers);
    if (!sources.length) return [];

    const codes = Object.entries(sources[0].source.answers)
      .filter(([code]) => code !== "999")
      .sort(([, a], [, b]) => getAnswerDisplayOrder(a) - getAnswerDisplayOrder(b))
      .map(([code]) => code);

    const rows = [];
    for (const code of codes) {
      const label = codeLabels.value[code]
        || resolveLocalizedTitle(sources[0].source.answers[code]?.title, displayLocale.value)
        || code;
      for (const { pop, source } of sources) {
        const answer = source.answers[code];
        const base = getAdjustedBase(source);
        const row = {
          itemLabel: multiPop ? pop.label : label,
          groupLabel: multiPop ? label : null,
          itemId: effectiveQuestionId.value,
          itemN: base,
          questionId: effectiveQuestionId.value,
          popId: pop.id,
          popColor: pop.color,
          choiceColors: choiceColors.value,
        };
        rankingCodes.value.forEach((rank) => {
          const nb = getAnswerCount(answer?.ranks?.[rank]);
          row[`choice_${rank}`] = base > 0 ? nb / base : 0;
          row[`count_${rank}`] = nb;
          row[`label_${rank}`] = rankingLabels.value[rank];
        });
        rows.push(row);
      }
    }
    return rows;
  });

  // Stacked bar data for matrix questions
  const matrixStackedData = computed(() => {
    if (!isMatrix.value || !activeItems.value?.length) return [];
    const pops = matrixPopulations.value;
    const multiPop = pops.length > 1;
    const rows = [];

    for (const item of activeItems.value) {
      const itemData = chartsData.value[item.id];
      if (!itemData) continue;
      const itemLabel = resolveLocalizedTitle(item.item_title, displayLocale.value) || item.item_name;

      for (const pop of pops) {
        const source = pop.key ? itemData[pop.key] : itemData;
        if (!source?.answers) continue;

        const base = includeSkips.value ? getAdjustedBase(source) : null;
        const row = {
          itemLabel: multiPop ? pop.label : itemLabel,
          groupLabel: multiPop ? itemLabel : null,
          itemId: item.id,
          itemN: getAdjustedBase(source),
          questionId: item.id,
          popId: pop.id,
          popColor: pop.color,
          choiceColors: choiceColors.value,
        };
        Object.entries(source.answers).forEach(([code, answer]) => {
          const nb = getAnswerCount(answer);
          const sig = getSignificance(item.id, pop.key || "all", code);
          row[`choice_${code}`] = base != null && base > 0
            ? nb / base
            : getAnswerPercentage(answer);
          row[`count_${code}`] = nb;
          row[`label_${code}`] = codeLabels.value[code] || resolveLocalizedTitle(answer.title, displayLocale.value) || code;
          row[`sig_${code}`] = sig?.direction ?? null;
          row[`sigDetails_${code}`] = sig;
        });
        if (includeSkips.value) {
          const skippedNb = getSkippedCount(source);
          if (skippedNb > 0) {
            row["choice_999"] = base > 0 ? skippedNb / base : 0;
            row["count_999"] = skippedNb;
            row["label_999"] = "Skip";
          }
        }
        rows.push(row);
      }
    }

    return rows;
  });

  // Stacked bar data for non-matrix questions
  const singleStackedData = computed(() => {
    if (isMatrix.value || !populationsBarData.value.length) return [];
    return populationsBarData.value.map((pop) => {
      const row = {
        itemLabel: pop.label,
        questionId: effectiveQuestionId.value,
        popId: pop.popId,
        popColor: pop.color,
        choiceColors: choiceColors.value,
      };
      pop.data.forEach((d) => {
        row[`choice_${d.code}`] = d.value || 0;
        row[`count_${d.code}`] = d.count || 0;
        row[`label_${d.code}`] = d.label || d.code;
        row[`sig_${d.code}`] = d.significance || null;
        row[`sigDetails_${d.code}`] = d.significanceDetails || null;
      });
      return row;
    });
  });

  const stackedData = computed(() => {
    if (isRanking.value) return rankingStackedData.value;
    if (isMatrix.value) return matrixStackedData.value;
    return singleStackedData.value;
  });

  // Choice codes for stacked bars
  const matrixChoiceCodes = computed(() => {
    if (isRanking.value) return rankingCodes.value;
    let codes;
    if (isMatrix.value) {
      if (!activeItems.value?.length) return [];
      codes = Object.keys(codeLabels.value);
    } else {
      const firstPop = populationsBarData.value[0];
      if (!firstPop?.data?.length) return [];
      codes = firstPop.data.map((d) => d.code);
    }
    if (includeSkips.value && isMatrix.value && !codes.includes("999")) {
      const hasSkips = matrixStackedData.value.some((row) => row["choice_999"] > 0);
      if (hasSkips) codes = [...codes, "999"];
    }
    return codes;
  });

  // Choice labels keyed by code
  const matrixChoiceLabels = computed(() => {
    if (isRanking.value) return rankingLabels.value;
    const labels = {};
    if (isMatrix.value) {
      if (!activeItems.value?.length) return {};
      Object.entries(codeLabels.value).forEach(([code, label]) => {
        labels[code] = label;
      });
    } else {
      const firstPop = populationsBarData.value[0];
      if (!firstPop?.data?.length) return {};
      firstPop.data.forEach((d) => {
        labels[d.code] = d.label || d.code;
      });
    }
    if (includeSkips.value && matrixChoiceCodes.value.includes("999")) {
      labels["999"] = "Skip";
    }
    return labels;
  });

  // Matrix table data for table display
  const matrixTableData = computed(() => {
    if (!isMatrix.value || !activeItems.value?.length) return [];
    const pops = matrixPopulations.value;

    return activeItems.value.map((item) => {
      const itemData = chartsData.value[item.id];
      if (!itemData) return null;

      const choiceEntries = Object.entries(codeLabels.value);
      const itemChoices = choiceEntries.map(([code, label]) => ({
        code,
        label,
        populations: pops.map((pop) => {
          const source = pop.key ? itemData[pop.key] : itemData;
          const answer = source?.answers?.[code];
          const base = includeSkips.value ? getAdjustedBase(source) : null;
          const nb = getAnswerCount(answer);
          const sig = getSignificance(item.id, pop.key || "all", code);
          return {
            value: base != null && base > 0 ? nb / base : getAnswerPercentage(answer),
            count: nb,
            significance: sig?.direction ?? null,
            significanceDetails: sig,
          };
        }),
      }));

      if (includeSkips.value) {
        const hasSkips = pops.some((pop) => {
          const source = pop.key ? itemData[pop.key] : itemData;
          return getSkippedCount(source) > 0;
        });
        if (hasSkips) {
          itemChoices.push({
            code: "999",
            label: "Skip",
            populations: pops.map((pop) => {
              const source = pop.key ? itemData[pop.key] : itemData;
              const base = includeSkips.value ? getAdjustedBase(source) : null;
              const nb = getSkippedCount(source);
              return {
                value: base != null && base > 0 ? nb / base : 0,
                count: nb,
              };
            }),
          });
        }
      }

      return {
        itemId: item.id,
        itemLabel: resolveLocalizedTitle(item.item_title, displayLocale.value) || item.item_name,
        itemN: getAdjustedBase(itemData),
        choices: itemChoices,
      };
    }).filter(Boolean);
  });

  return {
    stackedData,
    matrixChoiceCodes,
    matrixChoiceLabels,
    matrixTableData,
  };
}
